import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

const Home = () => {
  const services = [
    {
      title: "Golf Academy",
      description: "Personalized coaching programs with certified PGA professionals. Transform your game through systematic training and modern techniques.",
      imageUrl: "https://www.marangaroogolf.com.au/wp-content/uploads/sites/28/2024/06/nathan-jennings-FmhOm8RjoW8-unsplash-scaled.jpg",
      icon: "🏌️"
    },
    {
      title: "Golf Simulators",
      description: "Cutting-edge technology with real-time swing analysis. Practice on world-class courses regardless of weather conditions.",
      imageUrl: "https://images.pexels.com/photos/31212256/pexels-photo-31212256.jpeg?cs=srgb&dl=pexels-swingzone-swingzone-2150575315-31212256.jpg&fm=jpg",
      icon: "📊"
    },
    {
      title: "Pro Shop",
      description: "Premium equipment with expert custom fitting. Find the perfect gear to enhance your performance on the course.",
      imageUrl: "https://media.istockphoto.com/id/2190489125/photo/golf-equipment-display-with-polished-clubs-and-branded-apparel.jpg?s=612x612&w=0&k=20&c=9b35UDab3welPpsudHhh5hX8JUxXtHNMueXAxohMa74=",
      icon: "🛒"
    }
  ];

  const uspItems = [
    {
      number: "01",
      title: "Affordable Access",
      description: "Demystifying golf through innovative membership models and pay-per-play options that make golf accessible to everyone."
    },
    {
      number: "02",
      title: "International Standards",
      description: "World-class facilities and coaching methodologies that match global benchmarks for excellence."
    },
    {
      number: "03",
      title: "Technology-Driven",
      description: "Advanced simulators, data analytics, and AI-powered training for precision improvement."
    },
    {
      number: "04",
      title: "Personalized Coaching",
      description: "Tailored training programs that adapt to individual needs, goals, and skill levels."
    }
  ];

  const blogPosts = [
    {
      id: 1,
      title: "Improving Your Swing Technique",
      excerpt: "Expert tips and techniques to perfect your golf swing and improve your game consistency.",
      category: "Golf Tips",
      imageUrl: "https://images.unsplash.com/photo-1591491659771-3f9777200049?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Z29sZiUyMHN3aW5nfGVufDB8fDB8fHww"
    },
    {
      id: 2,
      title: "Choosing the Right Golf Clubs",
      excerpt: "A comprehensive guide to selecting golf clubs that match your playing style and skill level.",
      category: "Equipment",
      imageUrl: "https://plus.unsplash.com/premium_photo-1663012820207-0b054fdd5b03?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8Z29sZiUyMGZpZWxkfGVufDB8fDB8fHww"
    },
    {
      id: 3,
      title: "Golf Course Strategy Guide",
      excerpt: "Learn how to read golf courses and develop winning strategies for different course layouts.",
      category: "Strategy",
      imageUrl: "https://plus.unsplash.com/premium_photo-1661773279734-9f1adc050d4e?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8Z29sZmluZ3xlbnwwfHwwfHx8MA%3D%3D"
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Beginner to Intermediate",
      testimonial: "The systematic coaching approach transformed my game completely. From a complete beginner to now playing confidently in tournaments.",
      rating: 5
    },
    {
      name: "Priya Sharma",
      role: "Corporate Executive",
      testimonial: "The simulators are fantastic for practice during weekdays. The data analytics helped me understand my swing better than ever before.",
      rating: 5
    },
    {
      name: "Arvind Patel",
      role: "Junior Golfer Parent",
      testimonial: "My son's progress in the youth development program has been incredible. The coaches are amazing with kids and very professional.",
      rating: 5
    }
  ];

  const stats = [
    { value: "1000+", label: "Students Trained", icon: "👨‍🎓" },
    { value: "24/7", label: "Simulator Access", icon: "🖥️" },
    { value: "15+", label: "Certified Coaches", icon: "🏌️" },
    { value: "98%", label: "Satisfaction Rate", icon: "⭐" }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="home-hero-section">
        <div className="hero-overlay"></div>
        
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="hero-video"
        >
          <source src="/images/golf.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <Container className="position-relative z-3 h-100">
          <Row className="h-100 align-items-center justify-content-center">
            <Col xs={12} lg={10} xl={8} className="text-center">
              <h1 className="hero-title mb-4">Making Golf Accessible. Worldwide.</h1>
              <p className="hero-subtitle mb-5">
                Discover a place where peace and relaxation will surround you from the moment you step through our doors.
              </p>
              <div className="hero-cta d-flex flex-column flex-sm-row justify-content-center gap-3">
                <a href="/services" className="btn btn-gold-filled btn-lg px-4 px-md-5 py-3">
                  Explore Services
                </a>
                <a href="/academy" className="btn btn-gold btn-lg px-4 px-md-5 py-3">
                  Join the Academy
                </a>
              </div>
            </Col>
          </Row>

          {/* Scroll Indicator - Fixed positioning for mobile */}
          <div className="scroll-indicator">
            <div className="scroll-text">Scroll to Explore</div>
            <div className="scroll-arrow">↓</div>
          </div>
        </Container>
      </section>

      {/* What We Do Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">Elevate Your Game</h2>
              <p className="section-subtitle">
                Comprehensive golf solutions designed to transform beginners into enthusiasts and enthusiasts into champions.
              </p>
            </Col>
          </Row>

          <Row className="g-4 justify-content-center">
            {services.map((service, index) => (
              <Col xs={12} md={6} lg={4} key={index}>
                <div className="card-modern h-100 d-flex flex-column">
                  <div 
                    className="card-img"
                    style={{ backgroundImage: `url(${service.imageUrl})` }}
                  >
                    <div className="card-img-icon">
                      {service.icon}
                    </div>
                  </div>

                  <div className="card-content d-flex flex-column flex-grow-1 p-4">
                    <h3 className="card-title mb-3">{service.title}</h3>
                    <p className="card-text mb-4 flex-grow-1">
                      {service.description}
                    </p>
                    <a 
                      href={`/services#${service.title.toLowerCase().replace(/\s+/g, '-')}`} 
                      className="btn btn-gold w-100 mt-auto"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Additional Services Info */}
          <Row className="mt-5">
            <Col xs={12} lg={8} className="mx-auto text-center">
              <div className="info-card p-4 p-md-5">
                <h4 className="info-title mb-3">Complete Golf Ecosystem</h4>
                <p className="info-text mb-4">
                  From your first swing to professional tournaments, we provide everything you need in one place.
                  Our integrated approach ensures seamless progression at every skill level.
                </p>
                <a href="/services" className="btn btn-gold-filled">Explore All Services</a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission & Vision Section */}
      <section className="section-padding bg-mission">
        <Container className="position-relative z-3">
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <div className="section-header mb-4">
                <h2 className="section-title mb-0">
                  <span className="text-gold">Our</span> Mission & Vision
                </h2>
              </div>
              <p className="section-subtitle text-white">
                Shaping the future of golf through innovation, excellence, and passion
              </p>
            </Col>
          </Row>

          <Row className="g-4 g-lg-5">
            <Col xs={12} lg={6} className="mb-4 mb-lg-0">
              {/* Mission Card */}
              <div className="mission-card card-modern h-100">
                <div className="card-content p-4 p-md-5">
                  <div className="d-flex align-items-start mb-4">
                    <div className="mission-icon me-4">M</div>
                    <div>
                      <h3 className="card-title mb-3">Mission</h3>
                      <p className="card-text">
                        To democratize golf by making premium experiences accessible and affordable for all enthusiasts,
                        breaking traditional barriers while maintaining uncompromising standards of excellence.
                      </p>
                    </div>
                  </div>
                  
                  <div className="card-stats mt-4 pt-4">
                    <Row className="g-3">
                      <Col xs={6}>
                        <div className="stat-box">
                          <div className="stat-number">1000+</div>
                          <div className="stat-label">Students Trained</div>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="stat-box">
                          <div className="stat-number">15+</div>
                          <div className="stat-label">Certified Coaches</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              {/* Vision Card */}
              <div className="vision-card card-modern h-100">
                <div className="card-content p-4 p-md-5">
                  <div className="d-flex align-items-start mb-4">
                    <div className="vision-icon me-4">V</div>
                    <div>
                      <h3 className="card-title mb-3">Vision</h3>
                      <p className="card-text">
                        To cultivate India's next generation of world-class golfers through innovative coaching,
                        cutting-edge technology, and a holistic development ecosystem that creates champions.
                      </p>
                    </div>
                  </div>
                  
                  <div className="card-stats mt-4 pt-4">
                    <Row className="g-3">
                      <Col xs={6}>
                        <div className="stat-box">
                          <div className="stat-number">24/7</div>
                          <div className="stat-label">Access</div>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="stat-box">
                          <div className="stat-number">98%</div>
                          <div className="stat-label">Satisfaction</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Call to Action */}
          <Row className="mt-5">
            <Col xs={12} lg={8} className="mx-auto text-center">
              <div className="cta-card p-4 p-md-5">
                <p className="cta-title mb-4">Ready to transform your golf journey?</p>
                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                  <a href="/academy" className="btn btn-gold-filled btn-lg px-4 px-md-5 py-3">
                    Join Our Academy
                  </a>
                  <a href="/contact" className="btn btn-gold btn-lg px-4 px-md-5 py-3">
                    Schedule a Tour
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why G4I Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">Why Choose Golf4International</h2>
              <p className="section-subtitle">
                Our unique approach combines tradition with innovation to deliver exceptional golf experiences.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {uspItems.map((item, index) => (
              <Col xs={12} lg={6} key={index}>
                <div className="usp-card card-modern h-100">
                  <div className="card-content p-4">
                    <div className="d-flex align-items-start">
                      <div className="usp-number me-4">{item.number}</div>
                      <div>
                        <h3 className="card-title mb-2">{item.title}</h3>
                        <p className="card-text mb-0">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Stats Section */}
          <Row className="mt-5 pt-5">
            <Col xs={12} className="text-center mb-4">
              <h3 className="section-title mb-3">Our Impact</h3>
            </Col>
            {stats.map((stat, index) => (
              <Col xs={6} md={3} className="text-center mb-4" key={index}>
                <div className="stats-item">
                  <div className="stats-icon mb-3">
                    {stat.icon}
                  </div>
                  <h3 className="stat-number mb-2">{stat.value}</h3>
                  <p className="stat-label">{stat.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Blog Preview Section */}
      <section className="section-padding bg-soft-sage">
        <Container>
          {/* Header centered */}
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">Latest Golf Insights</h2>
              <p className="section-subtitle">
                Stay updated with expert articles, tips, and news from the world of golf
              </p>
            </Col>
          </Row>

          {/* Blog Cards */}
          <Row className="g-4">
            {blogPosts.map((post) => (
              <Col xs={12} md={6} lg={4} key={post.id}>
                <div className="card-modern h-100 d-flex flex-column">
                  <div 
                    className="card-img"
                    style={{ backgroundImage: `url(${post.imageUrl})` }}
                  >
                    <div className="card-category">
                      {post.category}
                    </div>
                  </div>

                  <div className="card-content d-flex flex-column flex-grow-1 p-4">
                    <h3 className="card-title mb-3">{post.title}</h3>
                    <p className="card-text mb-4 flex-grow-1">
                      {post.excerpt}
                    </p>
                    <a href={`/blog/${post.id}`} className="btn btn-gold w-100 mt-auto">
                      Read Article
                    </a>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* View All Button */}
          <Row className="mt-4">
            <Col xs={12} className="text-center">
              <a href="/blog" className="btn btn-gold btn-lg px-5">View All Articles</a>
            </Col>
          </Row>

          {/* Blog Categories */}
          <Row className="mt-5">
            <Col xs={12} className="text-center">
              <div className="categories-card p-4 p-md-5">
                <h3 className="categories-title mb-4">Explore More Topics</h3>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  {["Technology", "Academy", "Business", "Sustainability"].map((category, index) => (
                    <a
                      key={index}
                      href={`/blog?category=${category}`}
                      className="btn btn-gold btn-category"
                    >
                      {category}
                    </a>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          {/* Newsletter Signup */}
          <Row className="mt-5">
            <Col xs={12} lg={8} className="mx-auto">
              <div className="newsletter-card p-4 p-md-5 text-center">
                <div className="newsletter-icon mb-4">
                  <span>✉️</span>
                </div>
                <h3 className="newsletter-title mb-3">Get Golf Tips & Updates</h3>
                <p className="newsletter-text mb-4">
                  Subscribe to our newsletter for exclusive golf tips, industry insights, tournament updates,
                  and special offers delivered to your inbox.
                </p>
                <div className="newsletter-form d-flex flex-column flex-md-row gap-2 justify-content-center align-items-stretch align-items-md-center w-100">
                  <input
                    type="email"
                    className="form-control newsletter-input flex-grow-1"
                    placeholder="Enter your email address"
                  />
                  <button 
                    className="btn btn-gold-filled newsletter-btn" 
                    type="button"
                  >
                    Subscribe
                  </button>
                </div>
                <p className="newsletter-note small mt-3 mb-0">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonial Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">What Our Members Say</h2>
              <p className="section-subtitle">
                Join hundreds of satisfied golfers who have transformed their game with us
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {testimonials.map((testimonial, index) => (
              <Col xs={12} md={6} lg={4} key={index}>
                <div className="testimonial-card card-modern h-100">
                  <div className="card-content p-4">
                    <div className="testimonial-header d-flex align-items-center mb-3">
                      <div className="testimonial-avatar me-3">
                        <span>👤</span>
                      </div>
                      <div>
                        <h5 className="testimonial-name mb-1">{testimonial.name}</h5>
                        <p className="testimonial-role small mb-0">{testimonial.role}</p>
                      </div>
                    </div>
                    <div className="testimonial-rating mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="star">★</span>
                      ))}
                    </div>
                    <p className="testimonial-text mb-0">"{testimonial.testimonial}"</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Global Vision Section - FIXED: Text visibility */}
      <section className="section-padding bg-global-vision">
        <Container>
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">Global Vision & Scale</h2>
              <p className="section-subtitle">
                Delivering premium golf experiences worldwide through cutting-edge technology and international expertise
              </p>
            </Col>
          </Row>

          <Row className="align-items-center g-4 g-lg-5">
            <Col xs={12} lg={6} className="mb-5 mb-lg-0">
              <div className="global-content">
                <div className="global-icons d-flex flex-wrap gap-4 mb-4">
                  <div className="global-icon-item text-center">
                    <div className="icon-circle bg-gold mb-2">
                      <span className="icon-text">🇮🇳</span>
                    </div>
                    <h4 className="global-icon-title mb-1">India First</h4>
                    <p className="global-icon-text mb-0">Homegrown Excellence</p>
                  </div>

                  <div className="global-icon-item text-center">
                    <div className="icon-circle bg-green mb-2">
                      <span className="icon-text">🌍</span>
                    </div>
                    <h4 className="global-icon-title mb-1">Global Standards</h4>
                    <p className="global-icon-text mb-0">World-Class Quality</p>
                  </div>
                </div>

                <p className="global-description mb-4">
                  Our expansion strategy focuses on bringing world-class facilities to major cities across India,
                  making premium golf experiences accessible through cutting-edge technology and international expertise.
                </p>

                <div className="global-locations d-flex flex-wrap gap-3">
                  <div className="location-card">
                    <h5 className="location-title mb-2">Current Locations</h5>
                    <div className="location-list">
                      <div className="location-item">🏢 Delhi Headquarters</div>
                      <div className="location-item">🏢 Mumbai Center</div>
                      <div className="location-item">🏢 Bengaluru Hub</div>
                    </div>
                  </div>

                  <div className="location-card">
                    <h5 className="location-title mb-2">Coming Soon</h5>
                    <div className="location-list">
                      <div className="location-item">📍 Chennai 2024</div>
                      <div className="location-item">📍 Kolkata 2024</div>
                      <div className="location-item">📍 Hyderabad 2025</div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="global-image rounded">
                <div className="image-overlay p-4">
                  <h4 className="image-title mb-2">International Partnerships</h4>
                  <p className="image-text mb-0">
                    Collaborating with global golf institutions for excellence
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CSS Styles */}
      <style jsx>{`
        /* ========== BASE STYLES ========== */
        :root {
          --primary-green: #1F3D2B;
          --muted-gold: #B59A5A;
          --soft-sage: #f0f7f3;
          --text-dark: #333333;
          --text-light: #666666;
          --white: #ffffff;
        }
        
        /* ========== TYPOGRAPHY ========== */
        .hero-title {
          font-size: clamp(2rem, 5vw, 3.5rem);
          color: var(--muted-gold);
          font-weight: 600;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
        }
        
        .hero-subtitle {
          font-size: clamp(1rem, 2.5vw, 1.5rem);
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 2.5rem;
          font-weight: 400;
        }
        
        .section-title {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          color: var(--primary-green);
          font-weight: 600;
          margin-bottom: 1rem;
          line-height: 1.2;
          font-family: 'Playfair Display', serif;
        }
        
        .section-subtitle {
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.5vw, 1.1rem);
          line-height: 1.6;
          max-width: 700px;
          margin: 0 auto;
          font-weight: 400;
        }
        
        .card-title {
          font-size: clamp(1.1rem, 1.8vw, 1.3rem);
          color: var(--primary-green);
          font-weight: 600;
          margin-bottom: 1rem;
          line-height: 1.4;
        }
        
        .card-text {
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }
        
        /* ========== SECTION SPACING ========== */
        .section-padding {
          padding: clamp(3rem, 6vw, 5rem) 0;
        }
        
        /* ========== BUTTONS ========== */
        .btn-gold-filled {
          background-color: var(--muted-gold);
          color: var(--primary-green);
          border: none;
          font-weight: 600;
          transition: all 0.3s ease;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          text-decoration: none;
          display: inline-block;
          cursor: pointer;
        }
        
        .btn-gold-filled:hover {
          background-color: #d4b56e;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
          text-decoration: none;
          color: var(--primary-green);
        }
        
        .btn-gold {
          background-color: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(5px);
          color: var(--muted-gold);
          border: 2px solid var(--muted-gold);
          font-weight: 600;
          transition: all 0.3s ease;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          text-decoration: none;
          display: inline-block;
          cursor: pointer;
        }
        
        .btn-gold:hover {
          background-color: rgba(181, 154, 90, 0.2);
          color: #d4b56e;
          border-color: #d4b56e;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
          text-decoration: none;
        }
        
        .btn-lg {
          padding: clamp(12px, 2vw, 14px) clamp(24px, 3vw, 32px);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
        }
        
        .btn-category {
          padding: 10px 24px;
          border-radius: 25px;
          font-size: 0.9rem;
        }
        
        /* ========== HERO SECTION ========== */
        .home-hero-section {
          height: 70vh;
          min-height: 600px;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          padding-top: 80px;
        }
        
        .hero-video {
          position: absolute;
          top: 50%;
          left: 50%;
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          transform: translate(-50%, -50%);
          object-fit: cover;
          z-index: 1;
        }
        
        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(180deg, rgba(31, 61, 43, 0.5) 0%, rgba(31, 61, 43, 0.7) 100%);
          z-index: 2;
        }
        
        .hero-cta {
          position: relative;
          z-index: 3;
        }
        
        /* ========== SCROLL INDICATOR - FIXED FOR MOBILE ========== */
        .scroll-indicator {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          text-align: center;
          z-index: 3;
          opacity: 0.8;
        }
        
        .scroll-text {
          font-size: 0.9rem;
          margin-bottom: 8px;
          white-space: nowrap;
        }
        
        .scroll-arrow {
          font-size: 1.5rem;
          animation: bounce 2s infinite;
        }
        
        /* ========== CARDS ========== */
        .card-modern {
          background: var(--white);
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          height: 100%;
        }
        
        .card-modern:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }
        
        .card-img {
          height: clamp(200px, 30vw, 240px);
          width: 100%;
          background-size: cover;
          background-position: center;
          position: relative;
        }
        
        .card-img-icon {
          position: absolute;
          top: 20px;
          left: 20px;
          width: clamp(50px, 8vw, 60px);
          height: clamp(50px, 8vw, 60px);
          background-color: var(--white);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .card-category {
          position: absolute;
          top: 15px;
          left: 15px;
          background-color: rgba(31, 61, 43, 0.9);
          color: var(--white);
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .card-content {
          flex: 1;
          padding: 1.5rem;
        }
        
        /* ========== MISSION & VISION SECTION ========== */
        .bg-mission {
          background: linear-gradient(135deg, var(--primary-green) 0%, #2D4A38 50%, var(--primary-green) 100%);
          position: relative;
        }
        
        .bg-mission .section-title {
          color: var(--white);
        }
        
        .bg-mission .section-subtitle {
          color: rgba(255, 255, 255, 0.9);
        }
        
        .text-gold {
          color: var(--muted-gold);
        }
        
        .text-white {
          color: var(--white);
        }
        
        .mission-icon,
        .vision-icon {
          width: clamp(50px, 8vw, 60px);
          height: clamp(50px, 8vw, 60px);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          font-weight: 600;
          color: var(--white);
          flex-shrink: 0;
        }
        
        .mission-icon {
          background: linear-gradient(135deg, var(--muted-gold), #D4B573);
          box-shadow: 0 8px 20px rgba(181, 154, 90, 0.3);
        }
        
        .vision-icon {
          background: linear-gradient(135deg, var(--primary-green), #2D4A38);
          box-shadow: 0 8px 20px rgba(31, 61, 43, 0.3);
        }
        
        .card-stats {
          border-top: 1px solid rgba(181, 154, 90, 0.2);
          margin-top: 1.5rem;
          padding-top: 1.5rem;
        }
        
        .stat-box {
          text-align: center;
          padding: 1rem;
          border-radius: 8px;
          background-color: rgba(31, 61, 43, 0.05);
          border: 1px solid rgba(181, 154, 90, 0.1);
        }
        
        .stat-number {
          color: var(--muted-gold);
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .stat-label {
          color: var(--primary-green);
          font-size: clamp(0.8rem, 1.2vw, 0.9rem);
          font-weight: 500;
        }
        
        .cta-card {
          background: linear-gradient(135deg, rgba(181, 154, 90, 0.1), rgba(31, 61, 43, 0.1));
          border: 1px solid rgba(181, 154, 90, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 12px;
        }
        
        .cta-title {
          font-size: clamp(1.1rem, 2vw, 1.25rem);
          color: var(--white);
          font-weight: 600;
          margin-bottom: 1.5rem;
        }
        
        /* ========== USP CARDS ========== */
        .usp-number {
          width: clamp(50px, 8vw, 60px);
          height: clamp(50px, 8vw, 60px);
          background-color: var(--soft-sage);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1rem, 1.8vw, 1.25rem);
          font-weight: 600;
          color: var(--muted-gold);
          flex-shrink: 0;
        }
        
        /* ========== STATS ========== */
        .stats-item {
          transition: transform 0.3s ease;
        }
        
        .stats-item:hover {
          transform: translateY(-5px);
        }
        
        .stats-icon {
          width: clamp(60px, 10vw, 70px);
          height: clamp(60px, 10vw, 70px);
          background-color: var(--soft-sage);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.5rem, 2.5vw, 1.8rem);
          margin-bottom: 1rem;
        }
        
        /* ========== INFO CARDS ========== */
        .info-card {
          background-color: var(--soft-sage);
          border-radius: 12px;
          transition: transform 0.3s ease;
        }
        
        .info-card:hover {
          transform: translateY(-5px);
        }
        
        .info-title {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(1.1rem, 1.8vw, 1.25rem);
        }
        
        .info-text {
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          line-height: 1.6;
        }
        
        /* ========== CATEGORIES CARD ========== */
        .categories-card {
          background-color: var(--white);
          border-radius: 12px;
          transition: transform 0.3s ease;
        }
        
        .categories-card:hover {
          transform: translateY(-5px);
        }
        
        .categories-title {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(1.1rem, 1.8vw, 1.25rem);
        }
        
        /* ========== NEWSLETTER CARD ========== */
        .newsletter-card {
          background-color: var(--white);
          border-radius: 12px;
          transition: transform 0.3s ease;
        }
        
        .newsletter-card:hover {
          transform: translateY(-5px);
        }
        
        .newsletter-icon {
          width: clamp(60px, 10vw, 70px);
          height: clamp(60px, 10vw, 70px);
          background-color: var(--soft-sage);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.5rem, 2.5vw, 1.8rem);
          color: var(--muted-gold);
          margin: 0 auto 1.5rem;
        }
        
        .newsletter-title {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(1.1rem, 1.8vw, 1.25rem);
        }
        
        .newsletter-text {
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          line-height: 1.6;
        }
        
        .newsletter-input {
          padding: 14px 16px;
          font-size: 1rem;
          min-height: 50px;
          border: 1px solid rgba(31, 61, 43, 0.2);
          border-radius: 4px;
          width: 100%;
          color: var(--text-dark);
        }
        
        .newsletter-input::placeholder {
          color: var(--text-light) !important;
          opacity: 1 !important;
        }
        
        .newsletter-input:-ms-input-placeholder {
          color: var(--text-light) !important;
        }
        
        .newsletter-input::-ms-input-placeholder {
          color: var(--text-light) !important;
        }
        
        .newsletter-btn {
          min-height: 50px;
          white-space: nowrap;
          padding: 14px 32px;
          color: var(--primary-green) !important;
        }
        
        .newsletter-btn:hover {
          color: var(--primary-green) !important;
        }
        
        .newsletter-note {
          color: var(--text-light);
        }
        
        /* ========== TESTIMONIAL CARDS ========== */
        .testimonial-avatar {
          width: clamp(40px, 8vw, 50px);
          height: clamp(40px, 8vw, 50px);
          background-color: var(--soft-sage);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.25rem, 2vw, 1.5rem);
        }
        
        .testimonial-name {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(0.95rem, 1.5vw, 1.05rem);
        }
        
        .testimonial-role {
          color: var(--text-light);
          font-size: clamp(0.8rem, 1.2vw, 0.9rem);
        }
        
        .testimonial-rating {
          color: var(--muted-gold);
        }
        
        .star {
          margin-right: 2px;
        }
        
        .testimonial-text {
          color: var(--text-light);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          line-height: 1.6;
          font-style: italic;
        }
        
        /* ========== GLOBAL VISION SECTION - FIXED TEXT COLORS ========== */
        .bg-global-vision {
          background-color: var(--soft-sage);
          position: relative;
          overflow: hidden;
        }
        
        /* Remove the background image overlay since it's causing visibility issues */
        .global-bg-overlay {
          display: none;
        }
        
        /* Global Vision text colors - using theme colors */
        .bg-global-vision .section-title {
          color: var(--primary-green);
        }
        
        .bg-global-vision .section-subtitle {
          color: var(--text-light);
        }
        
        .global-content {
          color: var(--text-dark);
        }
        
        .global-icons {
          display: flex;
          flex-wrap: wrap;
          gap: clamp(1rem, 2vw, 1.5rem);
        }
        
        .icon-circle {
          width: clamp(60px, 10vw, 80px);
          height: clamp(60px, 10vw, 80px);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }
        
        .bg-gold {
          background-color: var(--muted-gold);
        }
        
        .bg-green {
          background-color: var(--primary-green);
        }
        
        .icon-text {
          font-size: clamp(1.25rem, 2.5vw, 1.5rem);
          color: var(--white);
        }
        
        .global-icon-title {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(0.95rem, 1.5vw, 1.05rem);
          margin-top: 0.5rem;
        }
        
        .global-icon-text {
          color: var(--text-light);
          font-size: clamp(0.8rem, 1.2vw, 0.9rem);
        }
        
        .global-description {
          color: var(--text-dark);
          font-size: clamp(0.95rem, 1.5vw, 1.05rem);
          line-height: 1.6;
          margin-bottom: 2rem;
        }
        
        .global-locations {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .location-card {
          background-color: var(--white);
          border-radius: 12px;
          padding: 1.5rem;
          flex: 1;
          min-width: 200px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          transition: transform 0.3s ease;
        }
        
        .location-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }
        
        .location-title {
          color: var(--primary-green);
          font-weight: 600;
          font-size: clamp(0.95rem, 1.5vw, 1.05rem);
          margin-bottom: 1rem;
        }
        
        .location-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .location-item {
          color: var(--text-dark);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          line-height: 1.4;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .global-image {
          height: clamp(250px, 40vw, 400px);
          background: url('https://images.unsplash.com/photo-1592659762303-90081d34b277?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80') center/cover no-repeat;
          position: relative;
          overflow: hidden;
          border-radius: 12px;
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }
        
        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(31, 61, 43, 0.9));
          color: var(--white);
          padding: 1.5rem;
        }
        
        .image-title {
          font-weight: 600;
          font-size: clamp(1rem, 1.5vw, 1.1rem);
          color: var(--white);
        }
        
        .image-text {
          opacity: 0.9;
          font-size: clamp(0.8rem, 1.2vw, 0.9rem);
          color: var(--white);
        }
        
        /* ========== BACKGROUND COLORS ========== */
        .bg-soft-sage {
          background-color: var(--soft-sage);
        }
        
        /* ========== ANIMATIONS ========== */
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        /* ========== RESPONSIVE GRID GAPS ========== */
        .g-4 {
          --bs-gutter-x: clamp(1rem, 2vw, 1.5rem);
          --bs-gutter-y: clamp(1rem, 2vw, 1.5rem);
        }
        
        /* ========== MOBILE RESPONSIVE ========== */
        @media (max-width: 576px) {
          .home-hero-section {
            height: 65vh;
            min-height: 500px;
            padding-top: 100px;
          }
          
          .hero-title {
            margin-bottom: 1rem;
          }
          
          .hero-subtitle {
            margin-bottom: 2rem;
          }
          
          .hero-cta {
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 50px; /* Added space for scroll indicator */
          }
          
          .hero-cta .btn {
            width: 100%;
          }
          
          /* FIXED: Scroll indicator positioning for mobile */
          .scroll-indicator {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            color: white;
            text-align: center;
            z-index: 3;
            opacity: 0.8;
            width: 100%;
            padding: 0 15px;
          }
          
          .scroll-text {
            font-size: 0.8rem;
            margin-bottom: 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 200px;
            margin-left: auto;
            margin-right: auto;
          }
          
          .scroll-arrow {
            font-size: 1.2rem;
            animation: bounce 2s infinite;
          }
          
          .card-img {
            height: clamp(180px, 40vw, 220px);
          }
          
          .usp-card .d-flex {
            flex-direction: column;
            text-align: center;
          }
          
          .usp-number {
            margin: 0 auto 15px;
          }
          
          .mission-icon,
          .vision-icon {
            margin-right: 0;
            margin-bottom: 15px;
          }
          
          .global-locations {
            flex-direction: column;
          }
          
          .location-card {
            width: 100%;
          }
          
          .newsletter-form {
            flex-direction: column;
          }
          
          .newsletter-btn {
            width: 100%;
          }
          
          .newsletter-input {
            margin-bottom: 12px;
          }
        }
        
        @media (min-width: 577px) and (max-width: 768px) {
          .home-hero-section {
            height: 70vh;
            min-height: 550px;
            padding-top: 90px;
          }
          
          /* FIXED: Scroll indicator for tablet */
          .scroll-indicator {
            bottom: 30px;
          }
          
          .scroll-text {
            font-size: 0.85rem;
          }
          
          .scroll-arrow {
            font-size: 1.3rem;
          }
          
          .hero-cta {
            margin-bottom: 40px;
          }
          
          .card-img {
            height: clamp(200px, 35vw, 240px);
          }
          
          .global-icons {
            justify-content: center;
          }
        }
        
        @media (min-width: 769px) and (max-width: 992px) {
          .home-hero-section {
            height: 75vh;
            min-height: 600px;
            padding-top: 80px;
          }
          
          .scroll-indicator {
            bottom: 35px;
          }
          
          .card-img {
            height: clamp(220px, 30vw, 240px);
          }
        }
        
        @media (min-width: 993px) and (max-width: 1199px) {
          .home-hero-section {
            height: 80vh;
            min-height: 650px;
            padding-top: 60px;
          }
          
          .scroll-indicator {
            bottom: 40px;
          }
          
          .card-img {
            height: 240px;
          }
        }
        
        @media (min-width: 1200px) {
          .home-hero-section {
            height: 85vh;
            min-height: 700px;
            padding-top: 40px;
          }
          
          .scroll-indicator {
            bottom: 40px;
          }
          
          .card-img {
            height: 260px;
          }
        }
        
        /* ========== ACCESSIBILITY ========== */
        button:focus,
        input:focus,
        a:focus {
          outline: 2px solid var(--muted-gold);
          outline-offset: 2px;
        }
      `}</style>
    </>
  );
};

export default Home;